import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { useToast } from "../components/ToastContext";
import PillButton from "../components/button";
import httpClient from "../services/httpclient";
import { AuthResponse } from "../types";
const LOGIN = "/users/login";

type LoginResult = {
    success: boolean;
    token?: string;
    error?: string;
};

const userAuthManager = {
    async login(username: string, password: string, pin: string): Promise<LoginResult> {
        const data = { username, password, pin };
        try {
            const response = await httpClient.post<AuthResponse>(LOGIN, data);
            if (response.user) {
                localStorage.setItem("user", JSON.stringify(response.user));
                return { success: true, token: undefined };
            } else {
                return { success: false, error: "Invalid credentials" };
            }
        } catch (err: any) {
            return { success: false, error: err.message || "Network error" };
        }
    },
};

const schema = yup.object({
  username: yup.string().required("Please enter your username"),
  password: yup.string().required("Please enter your password"),
  pin: yup
    .string()
    .matches(/^\d{4}$/, "PIN must be a 4-digit number")
    .required("Please enter your 4-digit PIN"),
});

export default function UserLogin() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const handleLogin = (data: any) => {
    setLoading(true);
    userAuthManager
      .login(data.username, data.password, data.pin)
      .then((result) => {
        if (!result.success) {
          showToast("Login failed. Please check your credentials.", "error");
          setErrorMessage("Login failed. Please check your credentials.");
          setLoading(false);
        } else {
          showToast("Login successful!", "success");
          setLoading(false);
          navigate("/userdashboard");
        }
      })
      .catch((err) => {
        showToast(err.message, "error");
        setErrorMessage("Login failed. Please check your credentials.");
        setLoading(false);
      });
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-slate-800">
      {/* Left column */}
      <div className="hidden lg:flex w-1/2 bg-[#e6fafd] items-center justify-center">
        <div className="text-center px-10">
          <img src="/logo.png" alt="HT Immigration Solutions Logo" className="mx-auto mb-8 w-32 h-32 object-contain" />
          <h2 className="text-3xl font-bold text-[#0097b2] mb-4">Welcome to HT Immigration Solutions</h2>
          <p className="text-lg text-gray-700 mb-4">
            Please sign in to access your dashboard, view documents, and manage your profile.
          </p>
        </div>
      </div>
      {/* Right column */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white dark:bg-slate-800">
        <div className="bg-[#e6fafd] dark:bg-slate-800 w-[80%] sm:w-[400px] p-8 relative py-12 shadow-lg rounded">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            User Sign in
          </h2>
          <form onSubmit={handleSubmit(handleLogin)} className="space-y-4 text-left">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white">
                Username
              </label>
              <input
                {...register("username")}
                type="text"
                autoComplete="username"
                className={`bg-gray-50 text-sm rounded-lg w-full p-2.5 min-h-[44px] mt-1`}
                style={{
                  borderColor: errors.username ? "red" : "#c4c8cc",
                  borderWidth: "1px",
                }}
              />
              {errors.username && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.username.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white">
                Password
              </label>
              <input
                {...register("password")}
                type="password"
                autoComplete="current-password"
                className={`bg-gray-50 border text-sm rounded-lg w-full p-2.5 min-h-[44px] mt-1`}
                style={{
                  borderColor: errors.password ? "red" : "#c4c8cc",
                  borderWidth: "1px",
                }}
              />
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white">
                4-digit PIN
              </label>
              <input
                {...register("pin")}
                type="text"
                maxLength={4}
                className={`bg-gray-50 border text-sm rounded-lg w-full p-2.5 min-h-[44px] mt-1`}
                style={{
                  borderColor: errors.pin ? "red" : "#c4c8cc",
                  borderWidth: "1px",
                }}
              />
              {errors.pin && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.pin.message}
                </p>
              )}
            </div>
            <div className="min-h-[16px]">
              {errorMessage && (
                <p className="text-sm text-red-600 bg-red-100 border border-red-600 rounded p-4">
                  {errorMessage}
                </p>
              )}
            </div>
            <PillButton
              type="submit"
              variant="dark"
              size="xl"
              className="w-full font-medium mt-0 py-2 px-4 rounded-md transition duration-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 dark:bg-white dark:text-gray-900"
              disabled={loading}
            >
              {loading ? "Verifying..." : "Sign in"}
            </PillButton>
          </form>
        </div>
      </div>
    </div>
  );
}